import React from 'react';

import styled from '@emotion/styled';
import { Alert } from '@lightspeed/flame/Alert';
import { TextLink } from '@lightspeed/flame/Text';

import { createReactRenderer } from 'helpers/react-renderer';

import PreAuthPromoModal from './PreAuthPromoModal';

const USER_PREF_TYPE = 'ui_tab';
const USER_PREF_COMPONENT = 'register';
const USER_PREF_NAME = 'preauth_promo_banner';
const USER_PREF_DISMISSED_VALUE = 'hide';

const BannerWrapper = styled.div`
  margin-top: 18px;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  text-align: left !important;
`;

const StyledAlert = styled(Alert)`
  /*
   * The register sidebar applies global CSS resets (background, border, box-shadow,
   * button styles, link styles) that strip Flame's Alert appearance entirely.
   * These overrides restore the Alert's intended card design against
   * page-level resets that this component has no other way to escape from its mount context.
   */
  width: 100% !important;
  box-sizing: border-box !important;
  text-align: left !important;

  /* Restore Alert card appearance overridden by page CSS */
  background: #f5f7ff !important;
  border-top: 3px solid #2e61de !important;
  border-left: none !important;
  border-right: none !important;
  border-bottom: none !important;
  border-radius: 4px !important;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.06), 0 3px 6px 0 rgba(0, 0, 0, 0.03),
    0 1px 2px 0 rgba(0, 0, 0, 0.1) !important;
  padding: 8px 12px !important;

  /* Remove icon */
  .fl-alert__icon {
    display: none;
  }

  /* Remove left padding reserved for the icon */
  & > div > div {
    padding-left: 0 !important;
  }

  /* Title: 14px, keep on one line */
  & > div > div > p:first-of-type {
    font-size: 14px !important;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Body text: 13px */
  & > div > div > p:not(:first-of-type),
  & > div > div span,
  & > div > div div {
    font-size: 13px !important;
  }

  /* Add gap between content and close button */
  & > button {
    flex-shrink: 0 !important;
    margin-left: 8px !important;
    width: 1em !important;
    height: 1em !important;
    min-width: 0 !important;
    min-height: 0 !important;
    border-radius: 50% !important;
    border: none !important;
    padding: 0 !important;
    cursor: pointer !important;
    position: relative !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    background-color: rgba(47, 49, 55, 0.1) !important;

    svg {
      width: 0.5em !important;
      height: 0.5em !important;
      fill: black !important;
      position: absolute !important;
      top: 0.25em !important;
      left: 0.25em !important;
    }
  }

  /* Remove any border/outline on links from sidebar CSS */
  a {
    border: none !important;
    outline: none !important;
    font-weight: bold !important;
  }
`;

const PreAuthPromoBanner: React.FC = () => {
  const [isVisible, setIsVisible] = React.useState(false);
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  React.useEffect(() => {
    window.lsCloud.UserPreferences.get(USER_PREF_TYPE, USER_PREF_COMPONENT, USER_PREF_NAME).then(
      (value) => {
        setIsVisible(value !== USER_PREF_DISMISSED_VALUE);
      }
    );
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    window.lsCloud.UserPreferences.save(
      USER_PREF_TYPE,
      USER_PREF_COMPONENT,
      USER_PREF_NAME,
      USER_PREF_DISMISSED_VALUE
    );
  };

  const handleLearnMoreClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsModalOpen(true);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <>
      <BannerWrapper data-test="preauth-promo-banner">
        <StyledAlert
          type="info"
          title="New: Preauthorization"
          onClose={handleDismiss}
          data-test="preauth-promo-banner-alert"
        >
          <span data-test="preauth-promo-banner-description">
            Hold a deposit without charging your customer. Take the final payment later.
          </span>
          <div>
            <TextLink
              href="#"
              onClick={handleLearnMoreClick}
              data-test="preauth-promo-banner-learn-more"
            >
              Learn more
            </TextLink>
          </div>
        </StyledAlert>
      </BannerWrapper>

      <PreAuthPromoModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
};

export default PreAuthPromoBanner;

const RenderedComponent = createReactRenderer({
  component: PreAuthPromoBanner,
  selector: '[data-react-root="PreAuthPromoBanner"]',
});

history.onChangeState(
  [{ pathname: '/register.php' }],
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);
